<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center" style="gap: 16px;">
              <div class="header-icon-wrap">
                <i class="fe fe-file-text" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Submit Claim</h4>
                <small style="opacity: 0.8;">Complete claim details to proceed to service processing</small>
              </div>
            </div>
            <button class="btn btn-outline-light btn-sm" onclick="history.back()">
              <i class="fe fe-arrow-left mr-1"></i> Back
            </button>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">

          <!-- ── 1. CARE DETAILS ── -->
          <div class="card shadow-sm mb-4">
            <div class="form-card-header">
              <div class="section-number">1</div>
              <h6 class="mb-0 font-weight-bold text-white">Care Details</h6>
            </div>
            <div class="card-body px-4 pt-3">
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label class="small font-weight-bold text-secondary">Date of Care</label>
                  <input type="date" class="form-control" v-model="claim.seen_date" />
                </div>
                <div class="form-group col-md-6">
                  <label class="small font-weight-bold text-secondary">Authorization Code <span class="text-danger">*</span></label>
                  <select class="form-control" required v-model="claim.authorization_code">
                    <option value="">-- Select authorization code --</option>
                    <option v-for="auth_code in referrals" :key="auth_code.id" :value="auth_code.code_created">
                      {{ auth_code.code_created }} ({{ auth_code.code_usage }})
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- ── 2. PATIENT INFORMATION ── -->
          <div class="card shadow-sm mb-4">
            <div class="form-card-header">
              <div class="section-number">2</div>
              <h6 class="mb-0 font-weight-bold text-white">Patient Information</h6>
            </div>
            <div class="card-body px-4 py-3" v-if="summary && summary.enrollee">
              <div class="info-row">
                <span class="info-label"><i class="fe fe-user info-icon"></i> Surname</span>
                <span class="info-value">{{ summary.enrollee.firstname || '—' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label"><i class="fe fe-user info-icon"></i> First Name</span>
                <span class="info-value">{{ summary.enrollee.lastname || '—' }}</span>
              </div>
              <div class="info-row">
                <span class="info-label"><i class="fe fe-credit-card info-icon"></i> KGSHIA Number</span>
                <span class="info-value">
                  <span class="code-chip">{{ summary.enrollee.id_card_number || '—' }}</span>
                </span>
              </div>
              <div class="info-row">
                <span class="info-label"><i class="fe fe-phone info-icon"></i> Phone Number</span>
                <span class="info-value">{{ summary.enrollee.phone_number || '—' }}</span>
              </div>
            </div>
            <div class="card-body px-4 py-4 text-center text-muted" v-else>
              <i class="fe fe-loader" style="font-size: 1.5rem; opacity: 0.4;"></i>
              <p class="mb-0 mt-2 small">Loading patient information…</p>
            </div>
          </div>

          <!-- ── 3. CLINICAL DETAILS ── -->
          <div class="card shadow-sm mb-4">
            <div class="form-card-header">
              <div class="section-number">3</div>
              <h6 class="mb-0 font-weight-bold text-white">Clinical Details</h6>
            </div>
            <div class="card-body px-4 pt-3">
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label class="small font-weight-bold text-secondary">Select Diagnosis</label>
                  <v-select
                    v-model="summary.diagnosis.name"
                    label="name"
                    :required="!claim.diagnosis"
                    :options="diseases"
                    placeholder="Select diagnosis…"
                  />
                </div>
                <div class="form-group col-md-6">
                  <label class="small font-weight-bold text-secondary">Encounter ID</label>
                  <input type="text" disabled class="form-control" v-model="summary.healthrecord.encounter_id"
                    @change="verifyEncounter" />
                  <small class="text-success mt-1 d-block" v-if="summary && summary !== ''">
                    <i class="fe fe-check-circle mr-1"></i> Encounter verified for enrollee
                  </small>
                  <small class="text-danger mt-1 d-block" v-else>
                    <i class="fe fe-alert-circle mr-1"></i> Encounter not found
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- ── 4. REASON FOR CLAIM ── -->
          <div class="card shadow-sm mb-4">
            <div class="form-card-header">
              <div class="section-number">4</div>
              <h6 class="mb-0 font-weight-bold text-white">Reason for Claim</h6>
            </div>
            <div class="card-body px-4 pt-3">
              <div class="form-group mb-2">
                <label class="small font-weight-bold text-secondary">Select Reason</label>
                <v-select v-model="claim.treatment" label="name" :options="reasons" placeholder="Select reason for claim…" />
              </div>
              <div class="form-group" v-if="claim.treatment === 'Others'">
                <label class="small font-weight-bold text-secondary">Specify Reason</label>
                <textarea rows="4" class="form-control" v-model="claim.treatment"
                  placeholder="Describe the reason for this claim…"></textarea>
              </div>
            </div>
          </div>

          <!-- <div class="col-md-12">
            <b-form-checkbox
              v-model="claim.is_out_of_station"
              name="check-button"
              switch
            >
              Enrollee is an Out-of-Station <b>({{ claim.is_out_of_station }})</b>
            </b-form-checkbox>
          </div> -->

          <!-- ── SUBMIT ── -->
          <div class="card shadow-sm border-0">
            <div class="card-body px-4 py-3 d-flex justify-content-end" style="gap: 12px;">
              <button type="button" class="btn btn-outline-secondary px-4" @click="$router.go(-1)">Cancel</button>
              <button type="button" class="btn btn-submit px-5" @click="checkCode">
                <i class="fe fe-chevron-right mr-2"></i> Proceed to Drugs/Service Processing
              </button>
            </div>
          </div>

        </div>
      </section>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>

    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
// Import component
import Loading from "vue-loading-overlay";
// Import stylesheet
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: {
    Navbar,
    Loading,
  },
  data() {
    return {
      user: null,
      clients: "",
      client: "",
      encounter_id: "",
      encounter_details: "",
      summary: "",
      referrals: "",
      claims: "",
      diseases: "",
      searchkey: "",
      search_result: "",
      enrollee_details: "",
      edit: false,
      isLoading: false,
      fullPage: true,
      claim: {
        provider_id: "",
        client_id: "",
        diagnosis: "",
        treatment: "",
        is_out_of_station: false,
        client_name: "",
        seen_date: "",
      },
      reasons:[
  "Medical treatment for an illness or injury",
  "Prescription medications",
  "Diagnostic tests and screenings",
  "Surgical procedures",
  "Physical therapy or rehabilitation",
  "Mental health counseling or therapy",
  "Medical equipment or supplies",
  "Home health care",
  "Dental or vision care",
  "Emergency room visits",
  "Hospitalization",
  "Maternity care",
  "Preventive care services",
  "Specialist consultations",
  "Out-of-network provider services",
  "Ambulance services",
  "Hospice care",
  "Chiropractic care",
  "Occupational therapy",
  "Speech therapy",
  "Others"
]
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  computed: {
    randomNumber: function () {
      let authorization_code = Math.floor(
        Math.random() * (this.max - this.min + 1) + this.min
      );
      return authorization_code;
    },
  },
  methods: {
    getEnrollees() {
      if (this.user.type == "shis") {
        this.axios
          .get(`/api/v1/auth/getAgencyToUser/${this.user.id}`)
          .then((response) => {
            this.clients = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      } else {
        this.axios
          .get(`/api/v1/auth/getProviderToUser/${this.user.id}`)
          .then((response) => {
            this.clients = response.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },

    getClaims() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/claminByProvider${this.user.id}`)
        .then((response) => {
          this.claims = response.data.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getClient() {
      this.axios
        .get(`/api/v1/auth/user/${this.claim.client_id}`)
        .then((response) => {
          this.client = response.data.user;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    verifyEncounter(summary) {
      this.axios
        .post(`/api/v1/auth/verifyrecordbyencounterID`, {
          encounter_id: summary.id,
          patient_id: summary.enrollee.id,
        })
        .then((response) => {
          console.log(response);
          this.encounter_details = response.data;
        })
        .catch((error) => {
          console.error(error);
          this.encounter_details = "";
        });
    },

    getDiseases() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/diagnosis-agency/90`)
        .then((response) => {
          this.diseases = response.data.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    searchIDCard(summary) {
      this.isLoading = true;
      this.axios
        .post(`/api/v1/auth/getuserbyIdcard`, {
          id_card_number: summary.enrollee.id_card_number,
        })
        .then((response) => {
          this.search_result = response.data;

          console.log(response);
          this.$toasted.info("Searched Successfully", {
            position: "top-center",
            duration: 3000,
          });

          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
          this.$toasted.error("User not Found", {
            position: "top-center",
            duration: 3000,
          });
        });
    },
    makeClaim() {
      if (this.claim.authorization_code != "") {
        this.user = JSON.parse(localStorage.getItem("user"));
        if (this.user.type == "provider_employee") {
          // Add claim
          this.isLoading = true;
          this.axios
            .post("/api/v1/auth/claims", {
              provider_id: this.user.institutional_id,
              user_id: this.user.id,
              agency_id:  this.summary.enrollee.agency_id,
              client_name:
                this.summary.enrollee.type == "client"
                  ? this.summary.enrollee.id
                  : 0,
              dependent_id:
                this.summary.enrollee.type != "client"
                  ? this.summary.enrollee.id
                  : 0,
              seen_date: this.claim.seen_date,
              diagnosis: this.summary.diagnosis.id,
              treatment: this.claim.treatment,
              authorization_code: this.claim.authorization_code,
              is_out_of_station: this.claim.is_out_of_station,
            })

            .then((response) => {
              console.log(response);
              let id = response.data.id;
              this.isLoading = false;
              this.$router.push(`/service-processing-form/${id}`);
            })
            .catch((error) => {
              console.log(error.response);
            });
        } else {
          // Add claim
          this.isLoading = true;
          this.axios
            .post("/api/v1/auth/claims", {
              provider_id: this.user.id,
              user_id: this.user.id,
              agency_id: 95930,
              client_name: this.summary.enrollee.id,
              seen_date: this.claim.seen_date,
              diagnosis: this.claim.diagnosis.id,
              treatment: this.claim.treatment,
            })

            .then((response) => {
              console.log(response);
              let id = response.data.id;
              this.isLoading = false;
              // this.$breadstick.notify("Claim added Successfuly!", {position: "top-right"});
              this.$router.push(`/service-processing-form/${id}`);
            })
            .catch((error) => {
              console.log(error.response);
            });
        }
      } else {
        this.$toasted.error("Authorization Code cannot be blank", {
          position: "top-center",
          duration: 3000,
        });
      }
    },
    checkCode() {
      this.axios
        .post(`/api/v1/auth/checkAuthCode`, {
          authorization_code: this.claim.authorization_code,
        })
        .then((response) => {
          this.makeClaim();
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
          //  this.$toasted.info(`${error.message}`, {
          this.$toasted.error(`Authorization Code Expired or already used!`, {
            position: "top-center",
            duration: 3000,
          });
        });
    },
    verifyRef(summary) {
      this.axios
        .post(`/api/v1/auth/verifyReferal`, {
          searchkey: summary.enrollee.id_card_number,
        })
        .then((response) => {
          this.referrals = response.data.data;
          console.log(response);
          this.isLoading = false;
        })
        .catch((error) => {
          this.isLoading = false;
          console.error(error);
        });
    },
    getSingleSummary() {
      this.axios
        .get(`/api/v1/auth/service_summary/${this.$route.params.id}`)
        .then((response) => {
          this.summary = response.data;
          console.log(response);
          let summary = this.summary;
          this.searchIDCard(summary);
          this.verifyEncounter(summary);
          this.verifyRef(summary);
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },
  created() {
    this.getSingleSummary();
    this.getDiseases();
    this.getEnrollees();
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}
.header-icon-wrap {
  width: 48px; height: 48px;
  background: rgba(255,255,255,0.2);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
}
.form-card-header {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border-radius: 12px 12px 0 0;
}
.section-number {
  width: 26px; height: 26px; border-radius: 50%;
  background: rgba(255,255,255,0.25); color: #fff;
  font-size: 0.8rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.card { border-radius: 12px; border: 1px solid #e9ecef; overflow: hidden; }
.info-row {
  display: flex; align-items: center;
  padding: 10px 0; border-bottom: 1px solid #f1f5f9;
}
.info-row:last-child { border-bottom: none; }
.info-label {
  width: 180px; flex-shrink: 0;
  font-size: 0.82rem; font-weight: 600; color: #6b7280;
}
.info-icon { margin-right: 6px; color: #2563eb; }
.info-value { font-size: 0.9rem; color: #111827; font-weight: 500; }
.code-chip {
  background: #eff6ff; color: #1e3a5f;
  padding: 3px 10px; border-radius: 6px;
  font-size: 0.82rem; font-weight: 600; letter-spacing: 0.04em;
}
.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff; border: none; border-radius: 8px; font-weight: 600;
}
.btn-submit:hover { opacity: 0.9; color: #fff; }
.form-control:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.2);
}
</style>
